import BigNumber from 'bignumber.js';

// Instruments with a real, fixed tradeable tick size (forex pips, futures ticks)
// pass their own decimals through explicitly — that's authoritative and always
// wins. Crypto has no instrument identity in this app at all (no symbol field),
// and stocks has no per-symbol tick data either, so both fall back to this:
// round to SIG_FIGS significant figures, so a price is shown at a precision
// that's actually meaningful for its magnitude — floored at MIN_DECIMALS (a
// round dollar amount still shows cents) and capped at MAX_DECIMALS (matches
// backend PRICE_PRECISION/POINT_PRECISION, so this can never show more than the
// backend itself sends).
const SIG_FIGS = 6;
const MIN_DECIMALS = 2;
const MAX_DECIMALS = 8;

export const adaptiveDecimals = (num) => {
  if (!(num instanceof BigNumber) || num.isNaN() || !num.isFinite() || num.isZero()) return MIN_DECIMALS;
  const sigFigDp = num.abs().sd(SIG_FIGS, BigNumber.ROUND_HALF_EVEN).decimalPlaces() || 0;
  return Math.min(MAX_DECIMALS, Math.max(MIN_DECIMALS, sigFigDp));
};

// Price-level formatter shared by every calculator card + Pocket view.
// `decimals`, when known, always wins over the adaptive fallback. `emptyValue`
// lets each caller match its own existing placeholder for a missing value
// (Full cards use '0', Pocket uses '--') without changing unrelated behavior.
export const formatPriceLevel = (value, decimals = null, emptyValue = '0') => {
  if (value === '--') return '--';
  if (!value) return emptyValue;
  try {
    const num = value instanceof BigNumber ? value : new BigNumber(value);
    if (num.isNaN()) return typeof value === 'string' ? value : emptyValue;
    const dp = decimals != null ? decimals : adaptiveDecimals(num);
    const rounded = num.dp(dp, BigNumber.ROUND_HALF_EVEN);
    const formatOptions = { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 };
    return rounded.toFormat(rounded.decimalPlaces(), formatOptions);
  } catch (error) {
    console.error('Error formatting price level:', error);
    return emptyValue;
  }
};
